import ERROS from 'config/errosCatalogo.json';

/**
 * Todo erro que a tela mostra passa por aqui, identificado por um código do catálogo
 * (src/config/errosCatalogo.json — documentado em CATALOGO-DE-ERROS.md).
 *
 * O código existe para o relato: "deu erro nas notificações" não diz qual chamada falhou, nem com
 * que status; "NTF-101/500" diz as duas coisas. Por isso o usuário vê a mensagem E o código — só o
 * código o deixaria sem saber se tenta de novo, confere a internet ou corrige um campo.
 *
 * Formato `MOD-NNN`: MOD é o módulo (NTF, AGD, ADM...); a centena de NNN é a natureza —
 * 0xx validação de campo, 1xx falha ao carregar, 2xx falha ao gravar, 3xx dispositivo/arquivo,
 * 9xx inesperado. Validação (0xx) não exibe código: quem a vê corrige o campo, não liga para o
 * suporte, e o código só poluiria a linha embaixo do input.
 */

const MENSAGEM_DESCONHECIDA = 'Algo deu errado. Tente novamente.';

/** 0xx é validação de campo — catalogada, mas sem código na tela. */
export function exibeCodigo(codigo) {
	return !/^[A-Z]{3}-0\d\d$/.test(codigo);
}

/**
 * O status HTTP de uma falha, quando houve requisição: o da resposta, ou 0 quando ela nem chegou
 * (rede, CORS, timeout). Erro que não veio de requisição (exceção de código, câmera) não tem status.
 */
function statusDe(erro) {
	if (!erro || typeof erro !== 'object') return undefined;
	if (erro.response?.status) return erro.response.status;
	if (erro.isAxiosError || erro.request) return 0;
	return undefined;
}

/**
 * A mensagem que o backend escreveu para o usuário, quando escreveu. As regras de negócio chegam
 * como `{ mensagem }` com 400/404/409, e é ela que explica o que houve — o texto padrão do catálogo
 * esconderia justamente a informação útil. O código do frontend continua junto mesmo assim.
 */
function mensagemDoServidor(erro, aceitarTextoPuro) {
	const data = erro?.response?.data;
	// Alguns endpoints antigos (/usuario/cancelar, /perfil-profissional/desativar, cancelamento de
	// agendamento) respondem em texto puro — e é esse texto que avisa, por exemplo, que o gateway
	// falhou e a conta NÃO foi encerrada. Só vale onde a tela pede: em qualquer outro lugar um
	// corpo em texto é a página de erro do nginx.
	const mensagem = aceitarTextoPuro && typeof data === 'string' ? data : data?.mensagem;
	return typeof mensagem === 'string' && mensagem.trim() ? mensagem.trim() : null;
}

/**
 * Descreve um erro do catálogo.
 *
 * @param {string} codigo   código do catálogo, ex. 'NTF-101'
 * @param {*}      [erro]   a exceção capturada, se houver — dá o status e a mensagem do backend
 * @param {object} [opcoes]
 * @param {boolean} [opcoes.usarMensagemDoServidor=true] false onde a mensagem do backend não serve
 *        para o usuário (ex.: o login, que não pode dizer se o CPF existe)
 * @param {boolean} [opcoes.aceitarTextoPuro=false] true nos endpoints que respondem o erro em
 *        texto puro, e não em `{ mensagem }`
 * @param {string}  [opcoes.mensagem] texto montado na hora, para as poucas mensagens com número
 *        dentro ("Aguarde 30 segundos"); o catálogo guarda a forma genérica dela
 * @returns {{ codigo: string, referencia: string, mensagem: string, status: number|undefined, exibeCodigo: boolean }}
 */
export function descreverErro(codigo, erro, {
	usarMensagemDoServidor = true,
	aceitarTextoPuro = false,
	mensagem: mensagemPronta,
} = {}) {
	const entrada = ERROS[codigo];
	if (!entrada && process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line no-console
		console.error(`[erros] código fora do catálogo: ${codigo}`);
	}

	const status = statusDe(erro);
	const mensagem = mensagemPronta
		|| (usarMensagemDoServidor && mensagemDoServidor(erro, aceitarTextoPuro))
		|| entrada?.mensagem
		|| MENSAGEM_DESCONHECIDA;
	const referencia = status === undefined ? codigo : `${codigo}/${status}`;

	if (erro && exibeCodigo(codigo) && process.env.NODE_ENV !== 'test') {
		// Para casar o relato do usuário com o console do aparelho/navegador.
		// eslint-disable-next-line no-console
		console.warn(`[${referencia}]`, erro);
	}

	return { codigo, referencia, mensagem, status, exibeCodigo: exibeCodigo(codigo) };
}

/** A mensagem pronta para um toast ou um texto inline: "… Tente novamente. (Código NTF-101/500)". */
export function textoDoErro(codigo, erro, opcoes) {
	const d = descreverErro(codigo, erro, opcoes);
	return d.exibeCodigo ? `${d.mensagem} (Código ${d.referencia})` : d.mensagem;
}

export { ERROS };
